import t from 'react-translate';
import { css } from '@emotion/react';
import { novoEdBlue } from 'athena/styles/colors';
import ClickableContainer from 'components/clickable-container';
import NvIcon from 'shared/components/nv-icon';
import { gray5, white } from 'styles/global_defaults/colors';
import {
  doubleSpacing,
  halfSpacing,
  standardSpacing,
} from 'styles/global_defaults/scaffolding';
import { config } from '@config/pendo.config.json';
import { MentoringProgramMatchingType } from 'redux/schemas/models/mentoring-programs';

type MatchingTypeCardProps = {
  id: MentoringProgramMatchingType;
  icon: string;
  iconBackgroundColor: string;
  title: string;
  description: string;
  content: string;
  isSelected: boolean;
  onSelect: () => void;
};

const styles = (iconBackgroundColor: string) => css`
  border: 1px solid ${gray5};
  padding: ${standardSpacing}px;
  border-radius: ${halfSpacing}px;

  .matching-type-top {
    gap: ${standardSpacing}px;
  }

  .matching-type-icon {
    height: ${doubleSpacing}px;
    width: ${doubleSpacing}px;
    color: ${white};
    background: ${iconBackgroundColor};
    border-radius: 50%;
  }

  .matching-type-content {
    line-height: 18px;
  }

  &.selected {
    border: 2px solid ${novoEdBlue};
    box-shadow: 0px 2px 4px 0px rgba(58, 66, 77, 0.08);

    .matching-type-selected {
      height: 28px;
      width: 28px;
      color: ${white};
      background: ${novoEdBlue};
      border-radius: 50%;
    }
  }
`;

const MatchingTypeCard = ({
  id,
  icon,
  iconBackgroundColor,
  title,
  description,
  content,
  isSelected,
  onSelect,
}: MatchingTypeCardProps) => {
  const translationBase =
    t.MENTORING_PROGRAMS.SETTINGS.TABS.MATCHING_SETTINGS.MATCHING_TYPE
      .ARIA_LABEL;
  const dataQATag =
    config.pendo.athena.mentorshipProgram.settings.matching.selectMatchingType;

  return (
    <ClickableContainer
      css={styles(iconBackgroundColor)}
      onClick={onSelect}
      className={`d-flex flex-column h-100 ${isSelected ? 'selected' : ''}`}
      data-qa={dataQATag}
      data-qa-id={`${dataQATag}-${id}`}
      aria-label={translationBase.SELECT_MATCHING_TYPE(title)}
      aria-pressed={isSelected}
    >
      <div className='matching-type-top d-flex'>
        <div className='d-flex matching-type-icon align-items-center justify-content-center flex-shrink-0'>
          <NvIcon icon={icon} size='small' />
        </div>
        <div className='flex-grow-1'>
          <div className='heading-5 black'>{title}</div>
          <div className='text-regular gray-2'>{description}</div>
        </div>
        {isSelected && (
          <div className='d-flex matching-type-selected align-items-center justify-content-center flex-shrink-0'>
            <NvIcon icon='check' size='smallest' />
          </div>
        )}
      </div>
      <div className='text-small matching-type-content black mt-3'>
        {content}
      </div>
    </ClickableContainer>
  );
};

export default MatchingTypeCard;
